import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { newsArticles } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = useMemo(() => newsArticles.find((a) => a.id === id), [id]);

  useSeo({
    title: article ? `${article.title} - 久久爱影视资讯详情` : "资讯详情 - 久久爱",
    description: article
      ? article.summary
      : "久久爱影视资讯详情页，提供最新行业动态、深度影评与作品发布资讯。",
    keywords: article
      ? `${article.title},久久爱影视资讯,${article.category},影视报道`
      : "影视资讯,久久爱",
  });

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="flex max-w-md flex-col items-center gap-3 rounded-xl border border-background-200 bg-background-100 px-8 py-14 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-article-line text-3xl"></i>
            </span>
            <h1 className="text-lg font-bold text-foreground-900">
              抱歉，当前资讯内容不存在
            </h1>
            <p className="text-sm text-foreground-500">
              这篇文章可能已被移除，返回资讯列表看看其他内容吧。
            </p>
            <Link
              to="/news"
              className="mt-2 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              返回资讯列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = newsArticles
    .filter((a) => a.id !== article.id)
    .sort((a, b) => {
      const aSame = a.category === article.category ? 1 : 0;
      const bSame = b.category === article.category ? 1 : 0;
      return bSame - aSame;
    })
    .slice(0, 3);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />

      <main className="flex-1">
        <article className="mx-auto w-full max-w-3xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "影视资讯", path: "/news" }, { label: "资讯详情" }]} />

          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
            <span className="rounded-full bg-accent-100 px-2.5 py-0.5 font-medium text-accent-900">
              {article.category}
            </span>
            <span className="text-foreground-400">{article.publishedAt}</span>
            <span className="text-foreground-400">作者：{article.author}</span>
          </div>

          <h1 className="mt-3 text-2xl font-extrabold leading-snug text-foreground-950 md:text-4xl font-heading">
            {article.title}
          </h1>

          <p className="mt-4 border-l-4 border-primary-300 bg-background-100 p-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {article.summary}
          </p>

          <div className="mt-6 h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
            <img
              src={article.cover}
              alt={`${article.title}配图`}
              title={`${article.title} - 久久爱影视资讯`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-8 flex flex-col gap-5">
            {article.content.map((paragraph, index) => (
              <p
                key={index}
                className="text-sm leading-loose text-foreground-700 md:text-base"
              >
                {paragraph}
              </p>
            ))}
          </div>

          <p className="mt-8 text-xs text-foreground-400">
            本文为演示内容，仅供产品展示使用，不构成任何真实报道。
          </p>

          <div className="mt-8">
            <Link
              to="/news"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              返回资讯列表
            </Link>
          </div>
        </article>

        <section className="mx-auto w-full max-w-3xl px-4 pb-12 md:px-6">
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-foreground-950 md:text-2xl font-heading">
            <span className="h-5 w-1 rounded-full bg-accent-500 md:h-6"></span>
            相关文章
          </h2>
          <div className="mt-5 flex flex-col gap-3">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200 hover:bg-background-100"
              >
                <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={item.cover}
                    alt={`${item.title}配图`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-1 text-sm font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1 line-clamp-1 text-xs text-foreground-500">
                    {item.summary}
                  </p>
                </div>
                <i className="ri-arrow-right-s-line hidden text-xl text-foreground-400 transition-transform group-hover:translate-x-0.5 sm:block"></i>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}